import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { Box } from '~Styles';
import { useButton, useI18n } from '~Hooks';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import SearchSelect from '~ComponentsImproved/SearchSelect';
import { SETTINGS_ROW_MIN_HEIGHT } from '~ComponentsImproved/settingsRowMinHeight';
import styled from '@emotion/styled';

/** Still used by SelectListMulti, which renders a real inline multi-select grid/list */
export const SelectListWrapper = styled.div<{ dark: boolean; grid: boolean }>`
  background-color: ${({ dark }) => (dark ? 'transparent' : 'var(--colorWhite)')};
  border-radius: 8px;
  border: ${({ grid }) => (grid ? 'none' : '1px solid')};
  border-color: ${({ dark }) => (dark ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray200)')};
  display: ${({ grid }) => (grid ? 'grid' : 'block')};
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;

  & > div {
    border-radius: 8px;
    border-bottom: 1px solid;
    border-top: ${({ grid }) => (grid ? '1px solid' : 'none')};
    border-left: ${({ grid }) => (grid ? '1px solid' : 'none')};
    border-right: ${({ grid }) => (grid ? '1px solid' : 'none')};
    border-color: ${({ dark }) => (dark ? 'var(--colorBlueGray900)' : 'var(--colorBlueGray200)')};
  }
`;

/**
 * Plain div so it matches SettingsBlock's `& > div` selector, which applies the
 * outer padding and the divider border-bottom between rows (same as Toggle's Wrapper).
 */
const Wrapper = styled.div<{ compact: boolean }>`
  display: flex;
  align-items: center;
  min-height: ${({ compact }) => (compact ? 'auto' : `${SETTINGS_ROW_MIN_HEIGHT}px`)};
  width: 100%;
  box-sizing: border-box;
`;

/** Plain div (not a Button) so nothing overrides the padding — mirrors Toggle's Item exactly */
const Trigger = styled.div<{ compact: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  background-color: var(--colorBlueGray1100);
  padding: ${({ compact }) => (compact ? '8px 8px' : '12px 16px')};
  cursor: pointer;
`;

/** Label never shrinks; the value+arrow group is the one allowed to give up space */
const LabelHeading = styled(Heading)`
  flex-shrink: 0;
`;

/** min-width: 0 lets the truncated value inside actually shrink instead of overflowing */
const ValueAndArrow = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
  justify-content: flex-end;
`;

/**
 * min-width: 0 is required in addition to the parent's, since flex items default to
 * min-width: auto (their content size), which would otherwise ignore the ellipsis
 * and push the arrow out past the padding instead of truncating.
 */
const ValueText = styled(Paragraph)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface ListItem {
  id: 'NONE' | string;
  name: string;
  /** Shown below the name in the picker */
  description?: string;
  /** Icon src shown to the left of the name in the picker */
  icon?: string;
  disabled?: boolean;
}

interface Props {
  /** Label describing what is being selected, shown on the left */
  label: string;
  list: ListItem[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  /** Tighter padding for handheld screens */
  compact?: boolean;
  /** Render the list in the given order instead of sorting by name */
  keepOrder?: boolean;
  /** Forwarded to SearchSelect for remote name search */
  onSearchChange?: (term: string) => void;
  /** Value shown when nothing is selected, defaults to "None selected" */
  noneText?: string;
  /** Forwarded to SearchSelect to show a spinner while a remote search runs */
  isSearchLoading?: boolean;
}

const SelectList: FC<Props> = ({
  label,
  list,
  selectedId,
  onSelect,
  compact = false,
  keepOrder = false,
  onSearchChange,
  noneText,
  isSearchLoading,
}) => {
  const { i18n } = useI18n();
  const [showSearchSelect, setShowSearchSelect] = useState<boolean>(false);

  const openSearchSelect = useButton({ onClick: () => setShowSearchSelect(true) });

  const selectedItem = list.find((item) => item.id === selectedId);

  /** Sorted and mapped once per list change so SearchSelect's memoised rows stay untouched */
  const options = useMemo(
    () =>
      [...list]
        .sort((a, b) => {
          if (a.id === 'NONE') return -1;
          if (b.id === 'NONE') return 1;

          return keepOrder ? 0 : a.name.localeCompare(b.name);
        })
        .map((item) => ({
          id: item.id,
          label: item.name,
          description: item.description,
          icon: item.icon,
          italic: item.id === 'NONE',
          disabled: item.disabled,
        })),
    [list, keepOrder],
  );

  const handleSelect = useCallback(
    (id: string) => {
      onSelect(id);
      setShowSearchSelect(false);
    },
    [onSelect],
  );

  const closeSearchSelect = useCallback(() => setShowSearchSelect(false), []);

  return (
    <>
      <Wrapper compact={compact}>
        <Trigger {...openSearchSelect} compact={compact}>
          <LabelHeading level={5} bottom={0} color="var(--colorWhite)">
            {label}
          </LabelHeading>

          <ValueAndArrow gap="xs" display="flex" alignItems="center">
            <ValueText
              level={5}
              bottom={0}
              style={{ fontStyle: !selectedItem ? 'italic' : 'normal' }}
              color="var(--colorBlueGray300)">
              {selectedItem?.name ?? noneText ?? i18n('General.NoneSelected')}
            </ValueText>

            <Icon
              src={ArrowForwardIcon}
              height="20px"
              width="20px"
              color="var(--colorBlueGray200)"
              style={{ flexShrink: 0 }}
            />
          </ValueAndArrow>
        </Trigger>
      </Wrapper>

      {showSearchSelect && (
        <SearchSelect
          title={label}
          selectedId={selectedId ?? undefined}
          onSearchChange={onSearchChange}
          isSearchLoading={isSearchLoading}
          options={options}
          onSelect={handleSelect}
          onClose={closeSearchSelect}
        />
      )}
    </>
  );
};

export default memo(SelectList);
